import React, { useState, useEffect, useRef, useCallback } from 'react';
import TalktasticVoiceGame from '../TalktasticVoiceGame';
import { useLanguage } from '../contexts/LanguageContext';
import TipButton from './TipButton';

const Part1Training = ({ 
  username, 
  setShowCompletionModal, 
  setCurrentMessages,
  onRepeatTopic,
  onNewRound,
  onEndTraining
}) => {
  const { language, t } = useLanguage();
  
  // 练习相关状态
  const [prepTime, setPrepTime] = useState(0); // Part 1默认没有准备时间
  const [practiceTime, setPracticeTime] = useState(30);
  const [gamePhase, setGamePhase] = useState('idle');
  const [showRules, setShowRules] = useState(false);
  
  // 录音相关状态
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const fullTranscriptionRef = useRef('');

  // 对话消息状态
  const [part1Messages, setPart1Messages] = useState([
    {
      type: 'mentor',
      content: language === 'en' ? 
        'Hello! I am your Part 1 speaking practice assistant. Please click "Roll Dice" to select a speaking topic from the game board!' : 
        '你好！我是你的Part 1口语练习助手。请点击"摇步数"抽取飞行棋盘上对应的口语话题！',
      time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  
  // 创建聊天消息容器的引用
  const messagesEndRef = useRef(null);
  
  // 创建游戏组件的引用
  const gameRef = useRef(null);

  // 添加滚动到最新消息的功能
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // 当消息更新时自动滚动到底部
  useEffect(() => {
    scrollToBottom();
  }, [part1Messages]);

  // 添加一个新的 useEffect 钩子，确保在 part1Messages 变化时总是更新 currentMessages
  useEffect(() => {
    // 每当消息变化时，立即更新 currentMessages
    setCurrentMessages(part1Messages);
  }, [part1Messages, setCurrentMessages]);

  // 停止语音识别
  const stopSpeechRecognition = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error(language === 'en' ? 'Failed to stop speech recognition:' : '停止语音识别失败:', error);
      }
    }
    setIsRecording(false);
    isRecordingRef.current = false;
  }, [language]);
  
  // 处理倒计时
  useEffect(() => {
    if (gamePhase === 'idle' || gamePhase === 'completed') {
      return;
    }

    const countdown = setInterval(() => {
      setGamePhase(prevPhase => {
        if (prevPhase === 'prep') {
          return prevPhase; // 继续准备阶段
        } else {
          return prevPhase; // 继续练习阶段
        }
      });
    }, 1000);

    // 清理函数
    return () => {
      clearInterval(countdown);
      // 如果组件卸载时正在录音，确保停止录音
      if (isRecording) {
        stopSpeechRecognition();
      }
    };
  }, [gamePhase, practiceTime, isRecording, stopSpeechRecognition]);

  // 随机排列话题卡片的函数
  const randomizeTopics = useCallback(() => {
    if (gameRef.current) {
      // 先重置游戏状态
      if (typeof gameRef.current.resetGame === 'function') {
        gameRef.current.resetGame();
      }
      
      // 随机选择话题集合
      window.currentWordSetIndex = Math.floor(Math.random() * 3);
      
      // 调用updateVocab方法随机排列话题
      if (typeof gameRef.current.updateVocab === 'function') {
        gameRef.current.updateVocab();
      }
    }
  }, []);

  // 组件加载时，随机排列话题卡片
  useEffect(() => {
    // 每次组件挂载（用户切换到Part1时）都随机排列话题卡片
    randomizeTopics();
    
    // 清理函数 - 组件卸载时重置状态
    return () => {
      // 重置状态，确保下次挂载时是全新的状态
      setGamePhase('idle');
      setTranscription('');
      if (isRecordingRef.current) {
        stopSpeechRecognition();
      }
    };
  }, [randomizeTopics, stopSpeechRecognition]); // 依赖于randomizeTopics和stopSpeechRecognition

  // 添加对话完成后的重置函数
  useEffect(() => {
    // 监听完成弹窗关闭事件
    const handleCompletionModalClosed = (event) => {
      if (event.detail && event.detail.part === 'part1') {
        console.log(language === 'en' ? "Part1 training completion modal closed event detected, resetting training state" : "检测到Part1训练完成弹窗关闭事件，重置训练状态");
        // 重置状态到初始状态
        setPart1Messages([{
          type: 'mentor',
          content: language === 'en' ? 
            'Hello! I am your Part 1 speaking practice assistant. Please click "Roll Dice" to select a speaking topic from the game board!' : 
            '你好！我是你的Part 1口语练习助手。请点击"摇步数"抽取飞行棋盘上对应的口语话题！',
          time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
        }]);
        
        // 重置录音状态
        if (isRecordingRef.current) {
          try {
            recognitionRef.current?.stop();
          } catch (e) {
            console.error(language === 'en' ? "Failed to stop speech recognition:" : "停止语音识别失败:", e);
          }
          isRecordingRef.current = false;
          setIsRecording(false);
        }
        
        // 重置游戏状态
        setGamePhase('idle');
        setTranscription('');
        
        // 随机排列话题卡片
        randomizeTopics();
      }
    };

    // 添加事件监听
    window.addEventListener('completionModalClosed', handleCompletionModalClosed);

    // 清理函数
    return () => {
      window.removeEventListener('completionModalClosed', handleCompletionModalClosed);
    };
  }, [practiceTime, language, stopSpeechRecognition, randomizeTopics]);

  // 添加重置状态的事件监听 - MOVED AFTER stopSpeechRecognition definition
  useEffect(() => {
    const resetTrainingState = (event) => {
      // 确保只有当前组件接收到针对它的重置事件
      if (event.detail.part === 'part1') {
        // 重置消息
        setPart1Messages([{
          type: 'mentor',
          content: language === 'en' ? 
            'Hello! I am your Part 1 speaking practice assistant. Please click "Roll Dice" to select a speaking topic from the game board!' : 
            '你好！我是你的Part 1口语练习助手。请点击"摇步数"抽取飞行棋盘上对应的口语话题！',
          time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
        }]);
        
        // 重置游戏状态
        setGamePhase('idle');
        if (isRecordingRef.current) {
          stopSpeechRecognition();
        }
        
        // 重置录音和转录状态
        setTranscription('');
        
        // 随机排列话题卡片
        randomizeTopics();
      }
    };
    
    // 添加事件监听器
    window.addEventListener('resetTrainingState', resetTrainingState);
    
    // 清理函数
    return () => {
      window.removeEventListener('resetTrainingState', resetTrainingState);
    };
  }, [practiceTime, language, stopSpeechRecognition, randomizeTopics]); // 添加randomizeTopics到依赖数组

  // 处理话题选择
  const handlePart1TopicSelected = (topic, userMessage, isCompleted) => {
    if (topic) {
      const mentorMessage = {
        type: 'mentor',
        content: language === 'en' ? `Let's discuss this topic: ${topic}` : `让我们来讨论这个话题：${topic}`,
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
      };
      setPart1Messages(prev => [...prev, mentorMessage]);
    }

    if (userMessage) {
      // 如果是语音消息，使用特殊的展示方式
      if (userMessage.isVoice) {
        // 创建语音消息对象
        const voiceMessage = {
          type: userMessage.type,
          isVoice: true,
          content: userMessage.content, // 保存原始内容以便再次播放
          displayContent: language === 'en' ? '🔊 Voice message' : '🔊 语音消息', // 在聊天框中显示的内容
          time: userMessage.time || new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
          controls: userMessage.controls // 保留控制属性
        };
        setPart1Messages(prev => {
          const newMessages = [...prev, voiceMessage];
          // 更新当前消息记录
          setCurrentMessages(newMessages);
          // 如果游戏完成，显示完成弹窗
          if (isCompleted) {
            setTimeout(() => setShowCompletionModal(true), 500);
          }
          return newMessages;
        });
      } else {
        setPart1Messages(prev => {
          const newMessages = [...prev, userMessage];
          // 更新当前消息记录
          setCurrentMessages(newMessages);
          return newMessages;
        });
      }
    }
    
    // 如果游戏完成但没有用户消息
    if (isCompleted && !userMessage) {
      setCurrentMessages(part1Messages);
      setTimeout(() => setShowCompletionModal(true), 500);
    }
  };

  // 添加语音合成辅助函数
  const playSpeechSynthesis = (text) => {
    // 检查浏览器支持
    if (!window.speechSynthesis) {
      console.error('浏览器不支持语音合成');
      alert('您的浏览器不支持语音合成功能，请使用Chrome浏览器');
      return;
    }
    
    // 使用Web Speech API播放语音
    try {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US'; // 英语发音
      utterance.rate = 0.9; // 语速稍慢一些，更清晰
      
      window.speechSynthesis.cancel(); // 取消当前正在播放的语音
      window.speechSynthesis.speak(utterance);
      
      console.log('语音播放已启动');
    } catch (error) {
      console.error('语音合成错误:', error);
      alert('语音播放失败，请刷新页面重试');
    }
  };

  // 添加播放语音消息的函数
  const playVoiceMessage = (message) => {
    if (message.isVoice && message.content) {
      console.log('尝试播放语音消息:', message.content);
      
      // 如果消息有控制属性并且包含关键词，使用游戏组件的重播功能
      if (message.controls && message.controls.keyword && gameRef.current) {
        console.log('使用游戏组件重播问题，关键词:', message.controls.keyword);
        try {
          gameRef.current.replayQuestion(message.controls.keyword);
        } catch (error) {
          console.error('游戏组件重播失败，尝试使用原生语音合成:', error);
          // 如果游戏组件重播失败，回退到使用原生语音合成
          playSpeechSynthesis(message.content);
        }
      } else {
        // 否则使用Web Speech API播放语音
        playSpeechSynthesis(message.content);
      }
    }
  };

  // 添加显示原文的函数
  const showOriginalText = (message) => {
    if (message.isVoice && message.controls && message.controls.keyword && gameRef.current) {
      gameRef.current.showQuestionText(message.controls.keyword);
    }
  };

  useEffect(() => {
    // 清理函数 - 确保在组件卸载时停止录音
    return () => {
      if (isRecordingRef.current) {
        stopSpeechRecognition();
      }
    };
  }, [stopSpeechRecognition]);

  const tipContent = language === 'en' 
    ? '<strong>Part 1 has no preparation time by default. So the current board preparation time is zero. But if you\'re a beginner and want to practice, you can manually change the time settings.</strong>' 
    : '<strong>Part 1默认没有准备时间。所以当前棋盘格上的准备时间为零。但如果是新手想要练习，也可以手动更改时间设置</strong>';

  return (
    <>
      <TipButton content={tipContent} language={language} />
      <div className="part-content">
        <div className="training-container" style={{ width: '520px', marginRight: '30px' }}>
          <div className="game-area" style={{ width: '100%' }}>
            <TalktasticVoiceGame 
              ref={gameRef}
              initialScene="IELTS"
              showRules={showRules}
              setShowRules={setShowRules}
              interfaceLanguage={language === 'en' ? 'english' : 'chinese'}
              onTopicSelected={handlePart1TopicSelected}
              onGameComplete={(topic, message) => handlePart1TopicSelected(topic, message, true)}
              prepTime={prepTime}
              practiceTime={practiceTime}
            />
            
            {/* 录音状态指示器 - 确保在practice阶段始终显示 */}
            {gamePhase === 'practice' && (
              <div className="recording-status" style={{marginTop: '15px', marginBottom: '15px'}}>
                <div className="recording-indicator">
                  <div className="recording-wave"></div>
                  <div className="recording-wave"></div>
                  <div className="recording-wave"></div>
                  <div className="recording-wave"></div>
                  <div className="recording-wave"></div>
                </div>
                <span className="recording-text">{language === 'en' ? 'Recording voice' : '正在录制语音'}</span>
              </div>
            )}
            
            {/* 添加实时语音转录显示 */}
            {gamePhase === 'practice' && isRecording && (
              <div className="transcription-container" style={{marginTop: '20px'}}>
                <div className="transcription-content">
                  {transcription}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="right-panel" style={{ marginLeft: '400px', marginTop: '50px' }}>
          <div className="chat-header">
            <div className="chat-title">{language === 'en' ? 'Practice Dialogue' : '练习对话'}</div>
          </div>
          <div className="chat-messages">
            {part1Messages.map((message, index) => (
              <div key={index} className={`message ${message.type}`}>
                <div className="avatar-container">
                  <div className="user-name">{message.type === 'mentor' 
                    ? (language === 'en' ? 'Assistant' : '助手') 
                    : (username || (language === 'en' ? 'User' : '用户'))}</div>
                  <div className={`avatar ${message.type}`}>
                    {message.type === 'mentor' 
                      ? (language === 'en' ? 'A' : '助') 
                      : (username ? username[0] : (language === 'en' ? 'U' : '用'))}
                  </div>
                </div>
                <div className="message-wrapper">
                  <div className="message-content">
                    {message.isVoice ? (
                      // 语音消息的特殊渲染，添加控制按钮
                      <div className="voice-message-container">
                        <div className="voice-message" onClick={() => playVoiceMessage(message)}>
                          <span className="voice-icon">🔊</span>
                          <div className="voice-waveform">
                            <div className="wave"></div>
                            <div className="wave"></div>
                            <div className="wave"></div>
                            <div className="wave"></div>
                          </div>
                        </div>
                        {message.controls && message.controls.canShowText && (
                          <button 
                            onClick={() => showOriginalText(message)}
                            className="show-text-button"
                          >
                            {language === 'en' ? 'View Text' : '查看原文'}
                          </button>
                        )}
                      </div>
                    ) : (
                      message.content
                    )}
                  </div>
                  <div className="message-time">{message.time}</div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Part1Training; 